import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSkeleton from "./TableSkeleton";
import { Box, Checkbox, IconButton, Paper, TableFooter, TablePagination, TableSortLabel, Typography, Chip, Stack } from "@mui/material";
import { useTheme } from "@emotion/react";
import TablePaginationActions from "./TablePaginationActions";

const TableCommon = (props) => {
    const { 
        columns, 
        tableData, 
        loading, 
        pagination, 
        handlePagination, 
        count = 0, 
        checkbox = false, 
        onSelectAllClick, 
        numSelected = 0,
        emptyMessage = "No data found",
        showCount = true
    } = props;

    const paginationRequest = ({ pageNo, limit, sortBy, sortField, keywords }) => {
        handlePagination({
            pageNo: pageNo !== undefined ? pageNo : pagination.pageNo,
            limit: limit !== undefined ? limit : pagination.limit,
            sortBy: sortBy !== undefined ? sortBy : pagination.sortBy,
            sortField: sortField !== undefined ? sortField : pagination.sortField,
            keywords: keywords || pagination.keywords,
        })
    }

    const handleRequestSort = (property) => {
        const isAsc = pagination.sortField === property && pagination.sortBy === "ASC";
        paginationRequest({ sortBy: isAsc ? "DESC" : "ASC", sortField: property })
    };

    const renderEmptyState = () => (
        <TableRow>
            <TableCell colSpan={columns.length + (checkbox ? 1 : 0)} align="center" sx={{ py: 8 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
                        {emptyMessage}
                    </Typography>
                    {showCount && count > 0 && (
                        <Chip 
                            label={`Total: ${count} records`} 
                            size="small" 
                            sx={{ 
                                bgcolor: '#f5f5f5', 
                                color: '#666',
                                fontSize: '0.75rem'
                            }} 
                        />
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );

    return (
        <Paper sx={{ 
            width: '100%', 
            overflow: 'hidden', 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
        }}>
            {/* Table Header with Count */}
            {showCount && (
                <Box sx={{ 
                    p: 3, 
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: '#fafafa',
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1" sx={{ 
                            color: '#2c3e50', 
                            fontWeight: 600,
                            fontSize: '0.95rem',
                        }}>
                            Showing {count} records
                        </Typography>
                        {showCount && count > 0 && (
                            <Chip 
                                label={`Total: ${count} records`} 
                                size="small" 
                                sx={{ 
                                    backgroundColor: '#e3f2fd', 
                                    color: '#1976d2',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 24,
                                }} 
                            />
                        )}
                    </Stack>
                </Box>
            )}
            
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table sx={{ minWidth: 650 }} stickyHeader>
                    <TableHead>
                        <TableRow sx={{ 
                            th: { border: 0 }, 
                            backgroundColor: "#f8f9fa",
                            '& th': {
                                borderBottom: '2px solid #e0e0e0',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                color: '#2c3e50',
                                padding: '16px 12px',
                            }
                        }}>
                            {
                                checkbox && !loading && <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={numSelected > 0 && numSelected < count}
                                        onChange={onSelectAllClick}
                                        inputProps={{
                                            'aria-label': 'select all items',
                                        }}
                                    />
                                </TableCell>
                            }
                            {columns.map((column, index) => {
                                if (column.hide) {
                                    return false;
                                }
                                return (
                                    <TableCell
                                        align={column.align || "left"}
                                        sx={{ 
                                            p: 2, 
                                            fontSize: "14px", 
                                            fontWeight: "600", 
                                            minWidth: column.minWidth, 
                                            width: column?.width,
                                            color: '#333',
                                            '&:hover': {
                                                backgroundColor: column.sort ? '#f0f0f0' : 'transparent',
                                                cursor: column.sort ? 'pointer' : 'default'
                                            }
                                        }}
                                        key={index}
                                        sortDirection={pagination.sortField === column.field ? (pagination.sortBy === "ASC" ? "asc" : "desc") : false}
                                    >
                                        {
                                            column.sort
                                                ?
                                                <TableSortLabel
                                                    active={pagination.sortField === column.field}
                                                    direction={pagination.sortField === column.field ? (pagination.sortBy === "ASC" ? "asc" : "desc") : 'asc'}
                                                    onClick={() => handleRequestSort(column.field)}
                                                    sx={{
                                                        '&.MuiTableSortLabel-active': {
                                                            color: '#ED2024',
                                                            fontWeight: 700
                                                        },
                                                        '&:hover': {
                                                            color: '#ED2024'
                                                        },
                                                        justifyContent: column.align === 'center' ? 'center' : 'flex-start'
                                                    }}
                                                >
                                                    {column.title}
                                                </TableSortLabel>
                                                :
                                                <Box sx={{ 
                                                    textAlign: column.align || 'left',
                                                    width: '100%'
                                                }}>
                                                    {column.title}
                                                </Box>
                                        }
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableSkeleton rows={columns} />
                        ) : (
                            tableData || renderEmptyState()
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {/* Enhanced Pagination */}
            {
                pagination && count > 0
                    ?
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50, { label: 'All', value: count }]}
                        count={count}
                        rowsPerPage={pagination.limit}
                        page={pagination.pageNo}
                        SelectProps={{
                            inputProps: {
                                'aria-label': 'rows per page',
                            },
                            native: true,
                        }}
                        onPageChange={(e, newNo) => paginationRequest({ pageNo: newNo })}
                        onRowsPerPageChange={(e) => paginationRequest({ limit: e.target.value })}
                        ActionsComponent={TablePaginationActions}
                        sx={{
                            borderTop: '1px solid #f0f0f0',
                            backgroundColor: '#fafafa',
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: '0.875rem',
                                color: '#666'
                            }
                        }}
                    />
                    :
                    null
            }
        </Paper>
    );
};

export default TableCommon;
